import { useRef, useState } from "react";
import { ArrowUpRight, Ban, CheckSquare, Download, Flag, FolderPlus, Square } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import type { WorkingSet } from "@/hooks/useWorkingSets";

interface Props {
  selectedCount: number;
  totalCount: number;
  selectedIds: Set<string>;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  onRestoreSelection?: (ids: string[]) => void;
  onPromote: () => void;
  onDismiss: () => void;
  onFetchFullText?: () => void;
  onFlag?: () => void;
  workingSets?: WorkingSet[];
  onAddToWs?: (wsId: string) => void;
  onCreateWsFromSelected?: (name: string) => void;
}

export function SelectionBar({
  selectedCount,
  totalCount,
  selectedIds,
  onSelectAll,
  onDeselectAll,
  onRestoreSelection,
  onPromote,
  onDismiss,
  onFetchFullText,
  onFlag,
  workingSets,
  onAddToWs,
  onCreateWsFromSelected,
}: Props) {
  const [wsOpen, setWsOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const previousSelectionRef = useRef<Set<string> | null>(null);

  const allSelected = selectedCount > 0 && selectedCount >= totalCount;

  if (selectedCount === 0) return null;

  const handleCreate = () => {
    const trimmed = newName.trim();
    if (!trimmed || !onCreateWsFromSelected) return;
    onCreateWsFromSelected(trimmed);
    setNewName("");
    setCreating(false);
    setWsOpen(false);
  };

  return (
    <div
      className="flex items-center gap-2 rounded-md border px-3 py-1.5"
      style={{
        background: "rgba(137,180,250,0.08)",
        borderColor: "rgba(137,180,250,0.2)",
      }}
    >
      <span className="text-xs font-semibold" style={{ color: "#89b4fa" }}>
        {selectedCount} selected
      </span>
      <div className="mx-1 h-4 w-px" style={{ background: "rgba(137,180,250,0.2)" }} />

      <Button variant="outline" size="xs" onClick={onPromote}>
        <ArrowUpRight className="size-3.5" />
        Promote
      </Button>
      <Button variant="outline" size="xs" onClick={onDismiss}>
        <Ban className="size-3.5" />
        Dismiss
      </Button>
      {onFetchFullText && (
        <Button variant="outline" size="xs" onClick={onFetchFullText}>
          <Download className="size-3.5" />
          Fetch full-text
        </Button>
      )}
      {onFlag && (
        <Button
          variant="outline"
          size="xs"
          onClick={onFlag}
          className="text-amber-500/80 hover:text-amber-500"
        >
          <Flag className="size-3.5" />
          Flag
        </Button>
      )}

      {/* Add to Working Set */}
      {onAddToWs && (
        <Popover open={wsOpen} onOpenChange={(v) => { setWsOpen(v); if (!v) setCreating(false); }}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="xs">
              <FolderPlus className="size-3.5" />
              Add to Working Set
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-52 p-1">
            {(workingSets ?? []).map((ws) => (
              <button
                key={ws.id}
                type="button"
                onClick={() => { onAddToWs(ws.id); setWsOpen(false); }}
                className="flex w-full items-center justify-between rounded px-2 py-1.5 text-sm hover:bg-accent"
              >
                <span>{ws.name}</span>
                <span className="text-xs text-muted-foreground">{ws.paper_count}</span>
              </button>
            ))}
            <div className="border-t mt-1 pt-1">
              {creating ? (
                <div className="flex gap-1 px-1">
                  <input
                    type="text"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleCreate()}
                    placeholder="Set name..."
                    className="flex-1 rounded border bg-background px-2 py-1 text-sm"
                    autoFocus
                  />
                  <Button size="xs" onClick={handleCreate} disabled={!newName.trim()}>
                    Save
                  </Button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setCreating(true)}
                  className="flex w-full items-center rounded px-2 py-1.5 text-sm hover:bg-accent"
                  style={{ color: "#89b4fa" }}
                >
                  + Create from Selected ({selectedCount})
                </button>
              )}
            </div>
          </PopoverContent>
        </Popover>
      )}

      <div className="mx-1 h-4 w-px" style={{ background: "rgba(137,180,250,0.2)" }} />

      <Button
        variant="ghost"
        size="xs"
        onClick={() => {
          if (allSelected) {
            // Toggle off: restore previous selection or deselect all
            if (previousSelectionRef.current && onRestoreSelection) {
              onRestoreSelection(Array.from(previousSelectionRef.current));
              previousSelectionRef.current = null;
            } else {
              onDeselectAll();
            }
          } else {
            // Toggle on: save current selection, then select all
            previousSelectionRef.current = new Set(selectedIds);
            onSelectAll();
          }
        }}
      >
        {allSelected ? (
          <CheckSquare className="size-3.5" />
        ) : (
          <Square className="size-3.5" />
        )}
        All
      </Button>
      <Button variant="ghost" size="xs" onClick={onDeselectAll}>
        <Square className="size-3.5" />
        None
      </Button>
    </div>
  );
}
